/**
 * ====================================================================
 * SYSTÈME DE LOGGING PEELOCHAT DASHBOARD
 * ====================================================================
 * Système de logging unifié avec contrôle facile pour le déploiement
 */

// ====================================================================
// CONFIGURATION
// ====================================================================

/**
 * Activer/désactiver les logs en fonction de l'environnement
 * Pour désactiver complètement les logs en production, mettez cette valeur à false
 */
const LOGGING_ENABLED = process.env.NODE_ENV !== 'production';

/**
 * Niveau de logging
 * - 0: Pas de logs
 * - 1: Erreurs seulement
 * - 2: Erreurs + Warnings
 * - 3: Erreurs + Warnings + Info
 * - 4: Tous les logs (Debug inclus)
 */
const LOG_LEVEL = process.env.NODE_ENV === 'production' ? 1 : 4;

// ====================================================================
// FONCTIONS DE LOGGING
// ====================================================================

/**
 * Logger principal avec préfixe et couleurs
 */
class Logger {
  constructor(prefix = '') {
    this.prefix = prefix;
  }

  /**
   * Log de débogage (niveau 4)
   */
  debug(message, ...args) {
    if (!LOGGING_ENABLED || LOG_LEVEL < 4) return;
    console.log(`🔧 [${this.prefix}] ${message}`, ...args);
  }

  /**
   * Log d'information (niveau 3)
   */
  info(message, ...args) {
    if (!LOGGING_ENABLED || LOG_LEVEL < 3) return;
    console.log(`ℹ️ [${this.prefix}] ${message}`, ...args);
  }

  /**
   * Log de succès (niveau 3)
   */
  success(message, ...args) {
    if (!LOGGING_ENABLED || LOG_LEVEL < 3) return;
    console.log(`✅ [${this.prefix}] ${message}`, ...args);
  }

  /**
   * Log d'avertissement (niveau 2)
   */
  warn(message, ...args) {
    if (!LOGGING_ENABLED || LOG_LEVEL < 2) return;
    console.warn(`⚠️ [${this.prefix}] ${message}`, ...args);
  }

  /**
   * Log d'erreur (niveau 1)
   */
  error(message, ...args) {
    if (!LOGGING_ENABLED || LOG_LEVEL < 1) return;
    console.error(`❌ [${this.prefix}] ${message}`, ...args);
  }

  /**
   * Log de progression/processus (niveau 3)
   */
  process(message, ...args) {
    if (!LOGGING_ENABLED || LOG_LEVEL < 3) return;
    console.log(`⚙️ [${this.prefix}] ${message}`, ...args);
  }

  /**
   * Log de réseau/API (niveau 3)
   */
  network(message, ...args) {
    if (!LOGGING_ENABLED || LOG_LEVEL < 3) return;
    console.log(`🌐 [${this.prefix}] ${message}`, ...args);
  }

  /**
   * Log de fichier/upload (niveau 3)
   */
  file(message, ...args) {
    if (!LOGGING_ENABLED || LOG_LEVEL < 3) return;
    console.log(`📁 [${this.prefix}] ${message}`, ...args);
  }

  /**
   * Log de timing/performance (niveau 4)
   */
  timing(message, ...args) {
    if (!LOGGING_ENABLED || LOG_LEVEL < 4) return;
    console.log(`⏱️ [${this.prefix}] ${message}`, ...args);
  }
}

// ====================================================================
// LOGGERS PRÉDÉFINIS
// ====================================================================

/**
 * Logger pour les uploads d'audio
 */
export const audioLogger = new Logger('AudioUpload');

/**
 * Logger pour les uploads d'image
 */
export const imageLogger = new Logger('ImageUpload');

/**
 * Logger pour PlaygroundView
 */
export const playgroundLogger = new Logger('PlaygroundView');

/**
 * Logger pour les flow elements
 */
export const flowLogger = new Logger('FlowElement');

/**
 * Logger pour les API calls
 */
export const apiLogger = new Logger('API');

/**
 * Logger générique
 */
export const logger = new Logger('PeeloChat');

// ====================================================================
// UTILITAIRES
// ====================================================================

/**
 * Créer un logger avec un préfixe personnalisé
 */
export function createLogger(prefix) {
  return new Logger(prefix);
}

/**
 * Logger avec ID unique pour traquer les opérations
 */
export function createTrackedLogger(prefix) {
  const id = Date.now() + '-' + Math.random().toString(36).substr(2, 6);
  return {
    id,
    logger: new Logger(`${prefix}-${id}`)
  };
}

/**
 * Mesurer le temps d'exécution d'une fonction
 */
export function measureTime(label, fn) {
  if (!LOGGING_ENABLED || LOG_LEVEL < 4) {
    return typeof fn === 'function' ? fn() : fn;
  }
  
  const start = performance.now();
  const result = typeof fn === 'function' ? fn() : fn;
  const end = performance.now();
  
  console.log(`⏱️ [${label}] Temps d'exécution: ${(end - start).toFixed(2)}ms`);
  
  return result;
}

/**
 * Logger conditionnel - seulement si une condition est vraie
 */
export function logIf(condition, level, message, ...args) {
  if (!condition) return;
  
  const tempLogger = new Logger('Conditional');
  tempLogger[level](message, ...args);
}

// ====================================================================
// WRAPPERS SIMPLES (Pour remplacer console.log directement)
// ====================================================================

/**
 * Remplacement direct pour console.log qui ne s'affiche pas en production
 */
export const consoleLog = (...args) => {
  if (LOGGING_ENABLED) {
    console.log(...args);
  }
};

/**
 * Remplacement direct pour console.error qui ne s'affiche pas en production
 * (Sauf si on veut garder les erreurs en prod, mais configurable ici)
 */
export const consoleError = (...args) => {
  if (LOGGING_ENABLED) {
    console.error(...args);
  }
};

/**
 * Remplacement direct pour console.warn qui ne s'affiche pas en production
 */
export const consoleWarn = (...args) => {
  if (LOGGING_ENABLED) {
    console.warn(...args);
  }
};

/**
 * Remplacement direct pour console.info qui ne s'affiche pas en production
 */
export const consoleInfo = (...args) => {
  if (LOGGING_ENABLED) {
    console.info(...args);
  }
};

// ====================================================================
// EXPORT PAR DÉFAUT
// ====================================================================

export default {
  logger,
  audioLogger,
  imageLogger,
  playgroundLogger,
  flowLogger,
  apiLogger,
  createLogger,
  createTrackedLogger,
  measureTime,
  logIf,
  consoleLog,
  consoleError,
  consoleWarn,
  consoleInfo,
  // Constantes utiles
  LOGGING_ENABLED,
  LOG_LEVEL
};
